/**
 * ModeMenuButton — Mobile-only chip + popover for the mode/challenge-level
 * picker. Renders next to the paperclip / model-picker icons inside the
 * chat input bar so the control sits where the user is composing, instead
 * of stacking as a second slider in the header.
 *
 * Desktop keeps the existing inline `<ModeSelector>` slider untouched —
 * this component is gated to mobile via CSS (display:none > 768px).
 *
 * Mirrors the popover pattern used by ModelSelector (native popover, click-
 * outside + Escape close, no Ant Popover dependency).
 */

import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import api from '@/services/api';
import './ModeMenuButton.css';

interface ModeConfig {
  enabled: boolean;
  title: string;
  description: string;
  default_index: number;
  modes: Array<{ label: string; description?: string; instruction: string }>;
}

interface ModeMenuButtonProps {
  config: ModeConfig;
  conversationId: string;
  activeIndex: number;
  onModeChange: (index: number) => void;
  disabled?: boolean;
}

export function ModeMenuButton({
  config,
  conversationId,
  activeIndex,
  onModeChange,
  disabled = false,
}: ModeMenuButtonProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Escape — same pattern as ModelSelector.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (!config.enabled || !config.modes?.length) return null;

  const handleSelect = async (index: number) => {
    setIsOpen(false);
    if (index === activeIndex || saving) return;

    setSaving(true);
    onModeChange(index);
    try {
      await api.put(`/conversations/${conversationId}/mode`, { mode_index: index });
    } catch (err) {
      console.error('Failed to save mode:', err);
      onModeChange(activeIndex);
    } finally {
      setSaving(false);
    }
  };

  const activeMode = config.modes[activeIndex];
  const activeLabel = activeMode?.label || '';

  return (
    <div className="mode-menu-button" ref={containerRef}>
      <button
        type="button"
        className={`mode-menu-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled || saving}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={config.title || t('chat.modePicker') || 'Mode'}
        title={config.title || activeLabel}
      >
        <span className="mode-menu-trigger-dot" aria-hidden="true" />
        <span dir="auto" className="mode-menu-trigger-label">{activeLabel}</span>
        <svg
          className="mode-menu-trigger-caret"
          viewBox="0 0 24 24"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="mode-menu-popover" role="listbox">
          {config.title && (
            <div className="mode-menu-popover-label" dir="auto">{config.title}</div>
          )}
          {config.modes.map((mode, idx) => {
            const isActive = idx === activeIndex;
            return (
              <button
                key={idx}
                type="button"
                role="option"
                aria-selected={isActive}
                className={`mode-menu-option ${isActive ? 'active' : ''}`}
                onClick={() => handleSelect(idx)}
                disabled={saving}
              >
                <span className="mode-menu-option-row">
                  <span className="mode-menu-option-label" dir="auto">{mode.label}</span>
                  {isActive && (
                    <svg
                      className="mode-menu-option-check"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                {mode.description && (
                  <span className="mode-menu-option-description" dir="auto">{mode.description}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
